import React from 'react';
import { twJoin } from 'tailwind-merge';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { TextBlockContainerTile } from './types/TextBlockContainerTile';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import ButtonLink from '../../shared/ButtonLink';
import OptimizedImage from '../../shared/OptimizedImage';

export default function TextBlockContainer({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: TextBlockContainerTile;
  trackingPrefix?: string;
}>) {
  const fullWidth = tile['full-width']?.value;
  const removeBottomMargin = tile['remove-bottom-margin']?.value;

  let maxWidthClasses;
  switch (tile['max-width']?.options?.[0]?.value) {
    case '7xl':
      maxWidthClasses = 'max-w-7xl';
      break;
    case '6xl':
      maxWidthClasses = 'max-w-6xl';
      break;
    case '5xl':
      maxWidthClasses = 'max-w-5xl';
      break;
    case '4xl':
      maxWidthClasses = 'max-w-4xl';
      break;
    default:
      maxWidthClasses = '';
      break;
  }

  let bgColorClass;
  switch (tile['bg-color']?.options?.[0]?.value) {
    case 'primary-5':
      bgColorClass = 'bg-primary-5';
      break;
    case 'primary-50':
      bgColorClass = 'bg-primary-50';
      break;
    case 'neutral-5':
      bgColorClass = 'bg-neutral-5';
      break;
    default:
      bgColorClass = '';
      break;
  }

  return (
    <div className={twJoin(!fullWidth && 'mx-auto max-w-screen-2xl px-4 sm:px-8')}>
      <div className={twJoin(bgColorClass, 'w-full', !fullWidth && 'rounded-xl')}>
        <div
          className={twJoin(
            'mx-auto',
            removeBottomMargin ? 'pt-8 sm:pt-14 lg:pt-16' : 'py-8 sm:py-14 lg:py-16',
            maxWidthClasses,
            bgColorClass && 'px-3 sm:px-8',
          )}
        >
          {tile.textblocks.chunks.map((chunk, i) => {
            const textBlockPiece = extractComponentFromChunk(
              chunk,
              'textblock',
              'PieceContent',
            )?.components;
            const text = extractComponentFromChunk(textBlockPiece, 'text', 'RichTextContent');
            const logo = extractComponentFromChunk(textBlockPiece, 'logo', 'ImageContent');
            const logoBottom = extractComponentFromChunk(
              textBlockPiece,
              'logo-bottom',
              'BooleanContent',
            )?.value;
            const logoAlignment = extractComponentFromChunk(
              textBlockPiece,
              'logo-alignment',
              'SelectionContent',
            )?.options?.[0]?.value;
            const fontSize = extractComponentFromChunk(
              textBlockPiece,
              'font-size',
              'SelectionContent',
            )?.options?.[0]?.value;
            const fontWeight = extractComponentFromChunk(
              textBlockPiece,
              'font-weight',
              'SelectionContent',
            )?.options?.[0]?.value;
            const fontColor = extractComponentFromChunk(
              textBlockPiece,
              'font-color',
              'SelectionContent',
            )?.options?.[0]?.value;
            const fontAlignment = extractComponentFromChunk(
              textBlockPiece,
              'font-alignment',
              'SelectionContent',
            )?.options?.[0]?.value;
            const bottomMargin = extractComponentFromChunk(
              textBlockPiece,
              'bottom-margin',
              'SelectionContent',
            )?.options?.[0]?.value;
            const bulletPointIcon = extractComponentFromChunk(
              textBlockPiece,
              'bullet-point-icon',
              'ImageContent',
            );
            let fontSizeClasses;
            let logoHeightClass;
            switch (fontSize) {
              case 'md':
                fontSizeClasses = 'text-md';
                logoHeightClass = 'h-6';
                break;
              case 'lg':
                fontSizeClasses = 'text-md sm:text-lg';
                logoHeightClass = 'h-6';
                break;
              case 'xl':
                fontSizeClasses = 'text-lg sm:text-xl';
                logoHeightClass = 'h-8';
                break;
              case '2xl':
                fontSizeClasses = 'text-xl sm:text-2xl';
                logoHeightClass = 'h-8';
                break;
              case '3xl':
                fontSizeClasses = 'text-2xl sm:text-3xl';
                logoHeightClass = 'h-7 sm:h-10';
                break;
              case '4xl':
                fontSizeClasses = 'text-3xl md:text-4xl';
                logoHeightClass = 'h-10 md:h-11';
                break;
              case '5xl':
                fontSizeClasses = 'text-4xl lg:text-5xl';
                logoHeightClass = 'h-11 lg:h-14';
                break;
              default:
                fontSizeClasses = '';
                logoHeightClass = 'h-6';
                break;
            }
            let fontWeightClass;
            switch (fontWeight) {
              case 'thin':
                fontWeightClass = 'font-thin';
                break;
              case 'light':
                fontWeightClass = 'font-light';
                break;
              case 'medium':
                fontWeightClass = 'font-medium';
                break;
              case 'black':
                fontWeightClass = 'font-black';
                break;
              default:
                fontWeightClass = '';
                break;
            }
            let fontColorClass;
            switch (fontColor) {
              case 'primary-100':
                fontColorClass = 'text-primary-100';
                break;
              case 'neutral-50':
                fontColorClass = 'text-neutral-50';
                break;
              default:
                fontColorClass = '';
                break;
            }
            let fontAlignmentClasses;
            switch (fontAlignment) {
              case 'left':
                fontAlignmentClasses = 'text-left';
                break;
              case 'center':
                fontAlignmentClasses = 'text-center w-fit mx-auto';
                break;
              case 'right':
                fontAlignmentClasses = 'text-right w-fit ml-auto';
                break;
              default:
                fontAlignmentClasses = 'text-left';
                break;
            }
            let bottomMarginClasses;
            switch (bottomMargin) {
              case 'sm':
                bottomMarginClasses = 'mb-4 sm:mb-6';
                break;
              case 'md':
                bottomMarginClasses = 'mb-6 sm:mb-8';
                break;
              case 'lg':
                bottomMarginClasses = 'mb-8 sm:mb-10';
                break;
              default:
                bottomMarginClasses = '';
                break;
            }
            let logoAlignmentClasses;
            switch (logoAlignment) {
              case 'center':
                logoAlignmentClasses = 'mx-auto';
                break;
              case 'right':
                logoAlignmentClasses = 'ml-auto';
                break;
            }

            return (
              <div
                key={`${i}textblock`}
                className={twJoin(
                  fontSizeClasses,
                  fontWeightClass,
                  fontColorClass,
                  bottomMarginClasses,
                  fontAlignmentClasses,
                )}
              >
                {!logoBottom && (
                  <OptimizedImage
                    imageComponent={logo}
                    className={twJoin('w-auto', logoAlignmentClasses, logoHeightClass)}
                    sizes="200px"
                    width={200}
                    height={30}
                  />
                )}
                <RichTextRenderer
                  blocks={text}
                  bulletPointIcon={bulletPointIcon?.images?.[0]}
                  className={twJoin(
                    fontAlignment === 'center' &&
                      text?.json?.[0]?.type === 'ordered-list' &&
                      'text-left',
                  )}
                />
                {logoBottom && (
                  <OptimizedImage
                    imageComponent={logo}
                    className={twJoin('w-auto', logoAlignmentClasses, logoHeightClass)}
                    sizes="200px"
                    width={200}
                    height={30}
                  />
                )}
              </div>
            );
          })}
          <ButtonLink
            ctaComponent={tile.cta}
            className="mx-auto w-fit"
            trackingPrefix={trackingPrefix}
          />
        </div>
      </div>
    </div>
  );
}
